/**
 * Fine-grained permissions for role-based access control.
 * Must match the backend Permission enum exactly.
 */
export enum Permission {
  // Financial permissions
  VIEW_PROJECT_BUDGETS = 'VIEW_PROJECT_BUDGETS',
  EDIT_PROJECT_BUDGETS = 'EDIT_PROJECT_BUDGETS',
  VIEW_INVOICES = 'VIEW_INVOICES',
  CREATE_INVOICES = 'CREATE_INVOICES',
  EDIT_INVOICES = 'EDIT_INVOICES',
  VIEW_EXPENSES = 'VIEW_EXPENSES',
  EDIT_EXPENSES = 'EDIT_EXPENSES',
  VIEW_FINANCIAL_REPORTS = 'VIEW_FINANCIAL_REPORTS',

  // Projects permissions
  VIEW_PROJECTS = 'VIEW_PROJECTS',
  CREATE_PROJECTS = 'CREATE_PROJECTS',
  EDIT_PROJECTS = 'EDIT_PROJECTS',
  DELETE_PROJECTS = 'DELETE_PROJECTS',

  // Clients permissions
  VIEW_CLIENTS = 'VIEW_CLIENTS',
  EDIT_CLIENTS = 'EDIT_CLIENTS',
  VIEW_CLIENT_CONTACTS = 'VIEW_CLIENT_CONTACTS',

  // Leads permissions
  VIEW_LEADS = 'VIEW_LEADS',
  CREATE_LEADS = 'CREATE_LEADS',
  EDIT_LEADS = 'EDIT_LEADS',
  DELETE_LEADS = 'DELETE_LEADS',

  // Schedule permissions
  VIEW_SCHEDULE = 'VIEW_SCHEDULE',
  EDIT_SCHEDULE = 'EDIT_SCHEDULE',

  // Tasks permissions
  VIEW_ALL_TASKS = 'VIEW_ALL_TASKS',
  ASSIGN_TASKS = 'ASSIGN_TASKS',

  // Inventory permissions
  VIEW_INVENTORY = 'VIEW_INVENTORY',
  EDIT_INVENTORY = 'EDIT_INVENTORY',

  // Work Orders permissions
  VIEW_WORK_ORDERS = 'VIEW_WORK_ORDERS',
  CREATE_WORK_ORDERS = 'CREATE_WORK_ORDERS',
  EDIT_WORK_ORDERS = 'EDIT_WORK_ORDERS',
  DELETE_WORK_ORDERS = 'DELETE_WORK_ORDERS',

  // Proposals permissions
  VIEW_PROPOSALS = 'VIEW_PROPOSALS',
  CREATE_PROPOSALS = 'CREATE_PROPOSALS',
  EDIT_PROPOSALS = 'EDIT_PROPOSALS',
  DELETE_PROPOSALS = 'DELETE_PROPOSALS',

  // Templates permissions
  VIEW_TEMPLATES = 'VIEW_TEMPLATES',
  EDIT_TEMPLATES = 'EDIT_TEMPLATES',

  // Admin permissions
  MANAGE_USERS = 'MANAGE_USERS',
  MANAGE_PERMISSIONS = 'MANAGE_PERMISSIONS',
  VIEW_SYSTEM_LOGS = 'VIEW_SYSTEM_LOGS',

  // Partner permissions
  VIEW_PARTNERS = 'VIEW_PARTNERS',
  MANAGE_PARTNERS = 'MANAGE_PARTNERS',
  INVITE_PARTNERS = 'INVITE_PARTNERS',
}

/**
 * Available colors for role badges.
 */
export const ROLE_COLORS = [
  'blue',
  'green',
  'red',
  'amber',
  'purple',
  'orange',
  'pink',
  'indigo',
  'emerald',
  'cyan',
] as const;

export type RoleColor = (typeof ROLE_COLORS)[number];

/**
 * Available icons for role badges (Heroicons names).
 */
export const ROLE_ICONS = [
  'ClipboardDocumentListIcon',
  'ShieldCheckIcon',
  'UsersIcon',
  'WrenchIcon',
  'BoltIcon',
  'SparklesIcon',
  'CubeIcon',
  'StarIcon',
  'TagIcon',
  'BuildingOfficeIcon',
  'TruckIcon',
  'BriefcaseIcon',
  'ChartBarIcon',
  'HomeIcon',
  'CogIcon',
  'CalendarIcon',
  'UserIcon',
  'AcademicCapIcon',
  'BeakerIcon',
  'ClipboardDocumentCheckIcon',
  'DocumentTextIcon',
  'CurrencyDollarIcon',
  'EyeIcon',
  'CheckBadgeIcon',
] as const;

export type RoleIcon = (typeof ROLE_ICONS)[number];

/**
 * Role document from the API.
 */
export interface Role {
  _id: string;
  name: string;
  description?: string;
  color: RoleColor;
  icon: RoleIcon;
  permissions: Permission[];
  company?: string;
  isSystemRole: boolean;
  isActive: boolean;
  slug?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * DTO for creating a new role.
 */
export interface CreateRoleDto {
  name: string;
  description?: string;
  color: RoleColor;
  icon: RoleIcon;
  permissions: Permission[];
  company?: string;
}

/**
 * DTO for updating an existing role.
 */
export interface UpdateRoleDto {
  name?: string;
  description?: string;
  color?: RoleColor;
  icon?: RoleIcon;
  permissions?: Permission[];
  isActive?: boolean;
}

/**
 * Permission metadata with labels and descriptions for UI display.
 */
export interface PermissionInfo {
  key: Permission;
  label: string;
  description: string;
}

/**
 * Permission category with metadata.
 */
export interface PermissionCategory {
  name: string;
  icon: string;
  permissions: PermissionInfo[];
}

/**
 * All permissions organized by category with labels and descriptions.
 */
export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    name: 'Financial',
    icon: 'CurrencyDollarIcon',
    permissions: [
      {
        key: Permission.VIEW_PROJECT_BUDGETS,
        label: 'View Project Budgets',
        description: 'Access to project financial information',
      },
      {
        key: Permission.EDIT_PROJECT_BUDGETS,
        label: 'Edit Project Budgets',
        description: 'Modify project budget amounts',
      },
      {
        key: Permission.VIEW_INVOICES,
        label: 'View Invoices',
        description: 'Access to invoice data and client billing',
      },
      {
        key: Permission.CREATE_INVOICES,
        label: 'Create Invoices',
        description: 'Generate new invoices for clients',
      },
      {
        key: Permission.EDIT_INVOICES,
        label: 'Edit Invoices',
        description: 'Modify and delete invoices, record payments',
      },
      {
        key: Permission.VIEW_EXPENSES,
        label: 'View Expenses',
        description: 'Access to expense records and reports',
      },
      {
        key: Permission.EDIT_EXPENSES,
        label: 'Edit Expenses',
        description: 'Create, modify and delete expense entries',
      },
      {
        key: Permission.VIEW_FINANCIAL_REPORTS,
        label: 'View Financial Reports',
        description: 'Access to profit/loss and financial analytics',
      },
    ],
  },
  {
    name: 'Projects',
    icon: 'FolderIcon',
    permissions: [
      {
        key: Permission.VIEW_PROJECTS,
        label: 'View Projects',
        description: 'Access to project information',
      },
      {
        key: Permission.CREATE_PROJECTS,
        label: 'Create Projects',
        description: 'Create new construction projects',
      },
      {
        key: Permission.EDIT_PROJECTS,
        label: 'Edit Projects',
        description: 'Modify project details and settings',
      },
      {
        key: Permission.DELETE_PROJECTS,
        label: 'Delete Projects',
        description: 'Remove projects from system',
      },
    ],
  },
  {
    name: 'Clients',
    icon: 'UsersIcon',
    permissions: [
      {
        key: Permission.VIEW_CLIENTS,
        label: 'View Clients',
        description: 'Access to client information',
      },
      {
        key: Permission.EDIT_CLIENTS,
        label: 'Edit Clients',
        description: 'Modify client details',
      },
      {
        key: Permission.VIEW_CLIENT_CONTACTS,
        label: 'View Client Contacts',
        description: 'Access to client phone and email',
      },
    ],
  },
  {
    name: 'Leads',
    icon: 'UserPlusIcon',
    permissions: [
      {
        key: Permission.VIEW_LEADS,
        label: 'View Leads',
        description: 'Access to lead/prospect information',
      },
      {
        key: Permission.CREATE_LEADS,
        label: 'Create Leads',
        description: 'Add new leads to the system',
      },
      {
        key: Permission.EDIT_LEADS,
        label: 'Edit Leads',
        description: 'Modify lead details and status',
      },
      {
        key: Permission.DELETE_LEADS,
        label: 'Delete Leads',
        description: 'Remove leads from system',
      },
    ],
  },
  {
    name: 'Schedule',
    icon: 'CalendarIcon',
    permissions: [
      {
        key: Permission.VIEW_SCHEDULE,
        label: 'View Schedule',
        description: 'Access to project schedules',
      },
      {
        key: Permission.EDIT_SCHEDULE,
        label: 'Edit Schedule',
        description: 'Modify project timelines',
      },
    ],
  },
  {
    name: 'Tasks',
    icon: 'ClipboardDocumentListIcon',
    permissions: [
      {
        key: Permission.VIEW_ALL_TASKS,
        label: 'View All Tasks',
        description: 'See tasks across all projects',
      },
      {
        key: Permission.ASSIGN_TASKS,
        label: 'Assign Tasks',
        description: 'Assign tasks to team members',
      },
    ],
  },
  {
    name: 'Inventory',
    icon: 'CubeIcon',
    permissions: [
      {
        key: Permission.VIEW_INVENTORY,
        label: 'View Inventory',
        description: 'Access to materials and supplies',
      },
      {
        key: Permission.EDIT_INVENTORY,
        label: 'Edit Inventory',
        description: 'Modify inventory quantities',
      },
    ],
  },
  {
    name: 'Work Orders',
    icon: 'WrenchScrewdriverIcon',
    permissions: [
      {
        key: Permission.VIEW_WORK_ORDERS,
        label: 'View Work Orders',
        description: 'Access to work order information',
      },
      {
        key: Permission.CREATE_WORK_ORDERS,
        label: 'Create Work Orders',
        description: 'Create new work orders',
      },
      {
        key: Permission.EDIT_WORK_ORDERS,
        label: 'Edit Work Orders',
        description: 'Modify work order details',
      },
      {
        key: Permission.DELETE_WORK_ORDERS,
        label: 'Delete Work Orders',
        description: 'Remove work orders from system',
      },
    ],
  },
  {
    name: 'Proposals',
    icon: 'DocumentTextIcon',
    permissions: [
      {
        key: Permission.VIEW_PROPOSALS,
        label: 'View Proposals',
        description: 'Access to proposal/quote information',
      },
      {
        key: Permission.CREATE_PROPOSALS,
        label: 'Create Proposals',
        description: 'Create new proposals and quotes',
      },
      {
        key: Permission.EDIT_PROPOSALS,
        label: 'Edit Proposals',
        description: 'Modify proposal details and line items',
      },
      {
        key: Permission.DELETE_PROPOSALS,
        label: 'Delete Proposals',
        description: 'Remove proposals from system',
      },
    ],
  },
  {
    name: 'Templates',
    icon: 'DocumentDuplicateIcon',
    permissions: [
      {
        key: Permission.VIEW_TEMPLATES,
        label: 'View Templates',
        description: 'Access to project and budget templates',
      },
      {
        key: Permission.EDIT_TEMPLATES,
        label: 'Edit Templates',
        description: 'Create, modify, and delete templates',
      },
    ],
  },
  {
    name: 'Partners',
    icon: 'BuildingOffice2Icon',
    permissions: [
      {
        key: Permission.VIEW_PARTNERS,
        label: 'View Partners',
        description: 'Access to partner company information',
      },
      {
        key: Permission.MANAGE_PARTNERS,
        label: 'Manage Partners',
        description: 'Manage partner relationships and project assignments',
      },
      {
        key: Permission.INVITE_PARTNERS,
        label: 'Invite Partners',
        description: 'Send invitations to new partner companies',
      },
    ],
  },
  {
    name: 'Admin',
    icon: 'CogIcon',
    permissions: [
      {
        key: Permission.MANAGE_USERS,
        label: 'Manage Users',
        description: 'Add, edit, and remove users',
      },
      {
        key: Permission.MANAGE_PERMISSIONS,
        label: 'Manage Permissions',
        description: 'Control user role permissions',
      },
      {
        key: Permission.VIEW_SYSTEM_LOGS,
        label: 'View System Logs',
        description: 'Access to system activity logs',
      },
    ],
  },
];

/**
 * Get all permissions as a flat array.
 */
export const ALL_PERMISSIONS: Permission[] = Object.values(Permission);

/**
 * Get Tailwind color classes for a role color.
 */
export const getRoleColorClasses = (color: RoleColor): { bg: string; text: string; border: string } => {
  const colorMap: Record<RoleColor, { bg: string; text: string; border: string }> = {
    blue: {
      bg: 'bg-blue-500',
      text: 'text-white',
      border: 'border-blue-500',
    },
    green: {
      bg: 'bg-green-500',
      text: 'text-white',
      border: 'border-green-500',
    },
    red: {
      bg: 'bg-red-500',
      text: 'text-white',
      border: 'border-red-500',
    },
    amber: {
      bg: 'bg-amber-500',
      text: 'text-white',
      border: 'border-amber-500',
    },
    purple: {
      bg: 'bg-purple-500',
      text: 'text-white',
      border: 'border-purple-500',
    },
    orange: {
      bg: 'bg-orange-500',
      text: 'text-white',
      border: 'border-orange-500',
    },
    pink: {
      bg: 'bg-pink-500',
      text: 'text-white',
      border: 'border-pink-500',
    },
    indigo: {
      bg: 'bg-indigo-500',
      text: 'text-white',
      border: 'border-indigo-500',
    },
    emerald: {
      bg: 'bg-emerald-500',
      text: 'text-white',
      border: 'border-emerald-500',
    },
    cyan: {
      bg: 'bg-cyan-500',
      text: 'text-white',
      border: 'border-cyan-500',
    },
  };
  return colorMap[color] || colorMap.blue;
};

/**
 * Get light Tailwind color classes for a role color (for backgrounds).
 */
export const getRoleLightColorClasses = (color: RoleColor): { bg: string; text: string } => {
  const colorMap: Record<RoleColor, { bg: string; text: string }> = {
    blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
    green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
    red: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
    amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300' },
    purple: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
    orange: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
    pink: { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-700 dark:text-pink-300' },
    indigo: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300' },
    emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300' },
    cyan: { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-cyan-300' },
  };
  return colorMap[color] || colorMap.blue;
};
